import React, { useState, useCallback, useRef } from "react";
import { css } from "glamor";
import { ChevronLeft, X } from "lucide-react";
import { Message } from "semantic-ui-react";
import Modal from "../library/Modal";
import { colors, spacing } from "../library/theme";
import ChangeCardFormContainer from "./ChangeCardFormContainer";

const modalClass = css({
  borderRadius: "14px !important",
  boxShadow: "0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 8px 10px -6px rgba(0, 0, 0, 0.1) !important",
  "&.ui.tiny.modal": {
    maxWidth: "480px",
  },
});

const headerWrapper = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const headerLeft = css({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
});

const headerTitle = css({
  fontFamily: "Montserrat, sans-serif !important",
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "24px",
  margin: 0,
  color: "#0F172A",
});

const closeButton = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 18,
  height: 18,
  cursor: "pointer",
  color: colors.greyDark,
  ":hover": { color: colors.black },
});

const backButton = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  borderRadius: 6,
  cursor: "pointer",
  color: colors.greyDark,
  ":hover": { color: colors.black },
});

const errorMessageStyle = { marginBottom: spacing.m };

export default function AddCardModal({ open, onClose, onBack, onSubmit, loading, requiresCpf = false, showSetAsDefault }) {
  const [submitError, setSubmitError] = useState(null);
  const formResetRef = useRef(null);

  const handleClose = useCallback(() => {
    if (formResetRef.current) formResetRef.current();
    setSubmitError(null);
    onClose();
  }, [onClose]);

  const handleBack = useCallback(() => {
    if (formResetRef.current) formResetRef.current();
    setSubmitError(null);
    if (onBack) onBack();
    else onClose();
  }, [onBack, onClose]);

  const handleSubmit = useCallback((cardData) => {
    setSubmitError(null);
    return onSubmit(cardData)
      .then(() => handleClose())
      .catch((error) => {
        const msg =
          error?.response?.body?.errors?.[0] ||
          error?.response?.body?.error ||
          "Ocorreu um erro ao atualizar o cartão. Tente novamente mais tarde.";
        setSubmitError(msg);
      });
  }, [onSubmit, handleClose]);

  if (!open) return null;

  return (
    <Modal open={open} onClose={handleClose} className={modalClass} size="tiny">
      <Modal.Header>
        <div className={headerWrapper}>
          <div className={headerLeft}>
            <div className={backButton} onClick={handleBack}>
              <ChevronLeft size={14} />
            </div>
            <h2 className={headerTitle}>Novo método de pagamento</h2>
          </div>
          <div className={closeButton} onClick={handleClose}>
            <X size={18} />
          </div>
        </div>
      </Modal.Header>

      <Modal.Content>
        {submitError && (
          <Message error visible content={submitError} style={errorMessageStyle} />
        )}
        <ChangeCardFormContainer
          onSubmit={handleSubmit}
          loading={loading}
          resetRef={formResetRef}
          requiresCpf={requiresCpf}
          showSetAsDefault={showSetAsDefault}
        />
      </Modal.Content>
    </Modal>
  );
}
